/**
 * Delivery zones + rules editorial. Static content. Includes the ZoneCard
 * helper used for both Queens tiers and the Staten Island zone.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { ZONES, SI_ZONE, RULES } from "./deliveryData";

function ZoneCard({ zone, expanded = false }) {
    const [showAll, setShowAll] = useState(false);
    return (
        <div
            className="bg-zen-bg border border-zen-line p-7"
            data-testid={`zone-${zone.id || "si"}`}
        >
            {zone.label && (
                <p className="text-[10px] uppercase tracking-[0.22em] text-zen-olive mb-3">
                    {zone.label}
                </p>
            )}
            <h3 className="font-serif text-2xl text-zen-ink font-light leading-tight mb-5">
                {zone.title}
            </h3>
            <dl className="grid grid-cols-3 gap-4 mb-5 text-center">
                <div>
                    <dt className="text-[10px] uppercase tracking-[0.22em] text-zen-ink-soft mb-1">
                        Min
                    </dt>
                    <dd className="font-serif text-xl text-zen-ink font-light">
                        {zone.min}
                    </dd>
                </div>
                <div>
                    <dt className="text-[10px] uppercase tracking-[0.22em] text-zen-ink-soft mb-1">
                        Fee
                    </dt>
                    <dd className="font-serif text-xl text-zen-olive font-light">
                        {zone.fee}
                    </dd>
                </div>
                <div>
                    <dt className="text-[10px] uppercase tracking-[0.22em] text-zen-ink-soft mb-1">
                        ETA
                    </dt>
                    <dd className="font-serif text-xl text-zen-ink font-light">
                        {zone.eta}
                    </dd>
                </div>
            </dl>
            <p className="text-[10px] uppercase tracking-[0.22em] text-zen-olive mb-2">
                {zone.zipCount} ZIPs
            </p>
            <p className="text-sm text-zen-ink-soft leading-relaxed mb-4">
                {zone.sample}
            </p>
            <button
                type="button"
                onClick={() => setShowAll((s) => !s)}
                className="text-[11px] uppercase tracking-[0.22em] text-zen-olive border-b border-zen-olive/40 pb-0.5 inline-flex items-center gap-2"
            >
                {showAll ? "Hide ZIPs" : `See all ${zone.zipCount} ZIPs`}{" "}
                <ArrowRight size={12} />
            </button>
            {(showAll || expanded) && (
                <p className="mt-4 text-xs font-mono text-zen-ink-soft leading-relaxed break-all">
                    {zone.zips.join(" · ")}
                </p>
            )}
        </div>
    );
}

const HOW_STEPS = [
    { step: "01", title: "Build the order.", body: "Browse the live menu, add to cart, and pick Delivery at checkout." },
    { step: "02", title: "Verify ID.", body: "Upload a photo of your government-issued ID — 21+ only. We re-card at the door." },
    { step: "03", title: "We pack it.", body: "Your order is hand-packed at the dispensary in opaque, smell-sealed bags." },
    { step: "04", title: "Driver arrives.", body: "You'll get a text when we're close. Pay cash or debit. ID checked at handoff." },
];

const PRODUCTS = [
    ["Flower", "Indica, sativa, hybrid — hand-trimmed top shelf."],
    ["Pre-rolls", "Singles, multi-packs, and infused."],
    ["Vapes", "Live resin, live rosin, distillate, pods, and disposables."],
    ["Edibles", "Artisan gummies, chocolates, drinks, micro-dosed (5 mg)."],
    ["Concentrates", "Live rosin, hash rosin, badder, budder."],
    ["Tinctures, topicals, accessories", "Everything else — same menu as in-store."],
];

export default function DeliveryZones() {
    return (
        <>
            {/* How it works */}
            <section
                data-testid="delivery-how"
                className="bg-zen-surface border-b border-zen-line"
            >
                <div className="max-w-[1200px] mx-auto px-6 sm:px-10 py-12">
                    <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-4">
                        How it works
                    </p>
                    <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-zen-ink leading-tight tracking-tight mb-8 max-w-3xl">
                        Four steps,{" "}
                        <span className="italic text-zen-olive">about an hour.</span>
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                        {HOW_STEPS.map((s) => (
                            <div
                                key={s.step}
                                className="p-7 bg-zen-bg border-l-2 border-zen-olive"
                            >
                                <p className="font-serif text-4xl text-zen-olive font-light leading-none mb-5">
                                    {s.step}
                                </p>
                                <h3 className="font-serif text-xl text-zen-ink font-light leading-tight mb-3">
                                    {s.title}
                                </h3>
                                <p className="text-sm text-zen-ink-soft leading-relaxed">
                                    {s.body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Queens zones */}
            <section
                data-testid="delivery-queens-zones"
                className="bg-zen-bg border-b border-zen-line"
            >
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-12">
                    <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-4">
                        · 01 ·
                    </p>
                    <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-zen-ink leading-tight tracking-tight mb-6 max-w-3xl">
                        Queens & Long Island delivery zones.
                    </h2>
                    <p className="text-sm sm:text-base text-zen-ink-soft max-w-2xl mb-6 leading-relaxed">
                        Three tiers, transparent pricing, honest ETAs. Hand-packed at our
                        flagship —{" "}
                        <strong className="text-zen-ink font-medium">
                            272-06 Union Turnpike, Queens, NY 11040
                        </strong>
                        .
                    </p>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                        {ZONES.map((z) => (
                            <ZoneCard key={z.id} zone={z} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Staten Island zone */}
            <section
                data-testid="delivery-si-zone"
                className="bg-zen-surface border-b border-zen-line"
            >
                <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-12">
                    <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-4">
                        · 02 ·
                    </p>
                    <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-zen-ink leading-tight tracking-tight mb-6 max-w-3xl">
                        Staten Island delivery zone.
                    </h2>
                    <p className="text-sm sm:text-base text-zen-ink-soft max-w-2xl mb-6 leading-relaxed">
                        From our Forest Avenue store at{" "}
                        <strong className="text-zen-ink font-medium">
                            2343 Forest Ave, Staten Island, NY 10303
                        </strong>
                        . Every active SI ZIP, one tier, fast.
                    </p>
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
                        <div className="lg:col-span-7">
                            <ZoneCard zone={SI_ZONE} expanded />
                        </div>
                        <div className="lg:col-span-5 bg-zen-bg p-7 border-l-2 border-zen-olive">
                            <h3 className="font-serif text-xl text-zen-ink font-light leading-tight mb-3">
                                Same calm energy on delivery.
                            </h3>
                            <p className="text-sm text-zen-ink-soft leading-relaxed mb-5">
                                Our Forest Ave store sits inside Roman Plaza — built
                                around a slower, quieter retail rhythm than the average
                                dispensary. Delivery carries that energy out the door:
                                hand-packed at the shop, opaque smell-sealed bags, ID at
                                the threshold, no rush.
                            </p>
                            <Link
                                to="/menu/staten-island"
                                className="text-[11px] uppercase tracking-[0.22em] text-zen-olive border-b border-zen-olive/40 pb-0.5 inline-flex items-center gap-2"
                            >
                                Browse Staten Island menu <ArrowRight size={12} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* What you can order */}
            <section className="bg-zen-bg border-b border-zen-line">
                <div className="max-w-[1100px] mx-auto px-6 sm:px-10 py-12">
                    <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-4">
                        · 03 ·
                    </p>
                    <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-zen-ink leading-tight tracking-tight mb-6 max-w-3xl">
                        What you can order.
                    </h2>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-3 text-sm sm:text-base text-zen-ink-soft leading-relaxed">
                        {PRODUCTS.map(([t, d]) => (
                            <li key={t} className="flex gap-3">
                                <span className="text-zen-olive shrink-0">·</span>
                                <span>
                                    <strong className="text-zen-ink font-medium">{t}</strong>{" "}
                                    — {d}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>

            {/* Rules */}
            <section
                data-testid="delivery-rules"
                className="bg-zen-surface border-b border-zen-line"
            >
                <div className="max-w-[1100px] mx-auto px-6 sm:px-10 py-12">
                    <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-4">
                        · 04 ·
                    </p>
                    <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-zen-ink leading-tight tracking-tight mb-6 max-w-3xl">
                        Delivery rules.
                    </h2>
                    <p className="text-sm sm:text-base text-zen-ink-soft max-w-2xl mb-6 leading-relaxed">
                        Required by New York State law. The faster you know them, the
                        faster delivery goes.
                    </p>
                    <ol className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {RULES.map((r, i) => (
                            <li
                                key={i}
                                className="flex gap-4 p-5 bg-zen-bg border-l-2 border-zen-olive"
                            >
                                <span className="font-serif text-2xl text-zen-olive font-light leading-none shrink-0">
                                    {String(i + 1).padStart(2, "0")}
                                </span>
                                <span className="text-sm text-zen-ink-soft leading-relaxed">
                                    {r}
                                </span>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* Closest legal */}
            <section className="bg-zen-bg border-b border-zen-line">
                <div className="max-w-[1100px] mx-auto px-6 sm:px-10 py-12">
                    <p className="text-[11px] uppercase tracking-[0.3em] text-zen-olive mb-4">
                        · 05 ·
                    </p>
                    <h2 className="font-serif font-light text-3xl sm:text-4xl lg:text-5xl text-zen-ink leading-tight tracking-tight mb-8 max-w-3xl">
                        Why we&rsquo;re the closest legal delivery for most of Long Island.
                    </h2>
                    <div className="space-y-5 text-sm sm:text-base text-zen-ink-soft leading-relaxed max-w-3xl">
                        <p>
                            Most Nassau and Suffolk towns opted out of cannabis retail
                            under New York&rsquo;s Marijuana Regulation and Taxation Act.
                            In plain terms — no licensed adult-use storefront operates
                            anywhere inside most of Long Island.
                        </p>
                        <p>
                            That leaves licensed Queens dispensaries as the lawful supply
                            for the western Long Island corridor. ZenZest sits right at
                            the Queens/Nassau border on Union Turnpike — geographically
                            closer to most of western Nassau than any other licensed
                            dispensary in the state.
                        </p>
                        <p>
                            Our delivery rotation is built around that geography. We
                            bundle Nassau and Suffolk routes by ZIP, leave New Hyde Park
                            with multiple orders per run, and prioritize members and
                            event-night customers in the local zone for tight windows.
                        </p>
                        <p>
                            You can verify any licensed New York operator at the{" "}
                            <a
                                href="https://cannabis.ny.gov/dispensary-location-verification"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline underline-offset-2 text-zen-ink hover:text-zen-olive"
                            >
                                NY OCM dispensary verification tool
                            </a>
                            . If a delivery service can&rsquo;t show you a license
                            number, it isn&rsquo;t legal — regardless of how it markets
                            itself.
                        </p>
                    </div>
                </div>
            </section>
        </>
    );
}
